import { ColDef, IRowNode } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import {
  Button,
  CollapsibleCard,
  ConfirmationDialogBox,
  DatePicker,
  Switch,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  AutoComplete,
  Notification,
  RadioGroup,
  SelectInput,
} from '@components';
import { CarrierAutoCompleteModel } from '@typeDefinitions/index';
import { createForm } from '@felte/solid';
import { CloseCircleIcon, PencilIcon } from '@atgfr8/icons';
import { validator } from '@felte/validator-yup';
import { createSignal, Show } from 'solid-js';
import { DateTime } from 'luxon';

import {
  carrierRestrictionFormSchema,
  CarrierRestrictionsFormState,
} from './validations';

const CARRIER_RESRICTION_NOTIFCATION = (
  <div class="flex flex-col">
    <div class="text-[#026EA1]">
      Restrict specific carriers from being assigned to this customer.
    </div>
    <div>
      Choose Hard Block to prevent assignment or Warning to allow assignment
      after user acknowledgement.
    </div>
  </div>
);

const REASON_TYPES = [
  'Service Failure',
  'Claim History',
  'Insurance Requirement',
  'Fraud',
];

const REASON_TYPE_OPTIONS = REASON_TYPES.map((reason) => ({
  label: reason,
  value: reason,
}));

const cellStyle = {
  padding: '10px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1.6 ',
};

const DELETE_RESTRICTION_DIALOG_ID = 'carrier-restriction-delete-dialog';

type CarrierRestrictionRecord = CarrierRestrictionsFormState & {
  status: 'Active' | 'Inactive';
};

const DUMMY_CARRIER_RESTRICTIONS: CarrierRestrictionRecord[] = [
  {
    carrierName: 'DAY & ROSS INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Hard Block',
    reasonType: 'Service Failure',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Active',
  },
  {
    carrierName: 'LANDSTAR RANGER, INC.',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Warning',
    reasonType: 'Claim History',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Inactive',
  },
  {
    carrierName: 'GLOBAL TRANSPORT INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Hard Block',
    reasonType: 'Insurance Requirement',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Inactive',
  },
  {
    carrierName: 'BY & T INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Warning',
    reasonType: 'Fraud',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Inactive',
  },
  {
    carrierName: 'GY & Well INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Warning',
    reasonType: 'Service Failure',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Inactive',
  },
  {
    carrierName: 'CAY & GOOD INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Hard Block',
    reasonType: 'Claim History',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Active',
  },
  {
    carrierName: 'CAY & GOOD INC',
    carrierMc: '134272',
    carrierDot: '00019176',
    restrictionType: 'Hard Block',
    reasonType: 'Claim History',
    reasonDescription:
      'Repeated service failures and poor customer experience.',
    effectiveFrom: '2026-03-09',
    effectiveTo: '',
    status: 'Active',
  },
];

const CarrierRestrictions = () => {
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  //   const fetchClaims = async () => {
  //     gridRef()?.api.showLoadingOverlay();
  //     if (params.id) {
  //       const res = await getCustomerClaimsHistory(params.id);
  //       res && setClaimHistory(res);
  //     }
  //     gridRef()?.api.hideOverlay();
  //   };

  //   let previousId = params.id;
  //   createEffect(async () => {
  //     if (previousId !== params.id) {
  //       previousId = params.id;
  //       await fetchClaims();
  //     }
  //   });

  const [editingRowId, setEditingRowId] = createSignal<string | null>(null);
  const [rowToDelete, setRowToDelete] =
    createSignal<CarrierRestrictionRecord | null>(null);

  const { form, data, setFields, errors, reset } =
    createForm<CarrierRestrictionsFormState>({
      initialValues: {
        carrierName: '',
        carrierMc: '',
        carrierDot: '',
        restrictionType: 'Hard Block',
        effectiveFrom: '',
        effectiveTo: '',
        reasonType: '',
        reasonDescription: '',
      },
      extend: validator({
        schema: carrierRestrictionFormSchema,
      }),
      onSubmit: (values) => {
        const rowId = editingRowId();
        if (rowId) {
          const node = gridRef()?.api.getRowNode(rowId);
          if (node?.data) {
            node.setData({ ...node.data, ...values });
            gridRef()?.api.refreshCells({ rowNodes: [node], force: true });
          }
          handleCancelEdit();
          return;
        }
        const newRow: CarrierRestrictionRecord = {
          ...values,
          status: 'Active',
        };
        gridRef()?.api.applyTransaction({ add: [newRow] });
        reset();
      },
    });

  const handleEditClick = (
    row: CarrierRestrictionRecord,
    node: IRowNode<CarrierRestrictionRecord>,
  ) => {
    setEditingRowId(node.id ?? null);
    setFields('carrierName', row.carrierName);
    setFields('carrierMc', row.carrierMc);
    setFields('carrierDot', row.carrierDot);
    setFields('restrictionType', row.restrictionType);
    setFields('effectiveFrom', row.effectiveFrom);
    setFields('effectiveTo', row.effectiveTo);
    setFields('reasonType', row.reasonType);
    setFields('reasonDescription', row.reasonDescription);
  };

  const handleCancelEdit = () => {
    reset();
    setEditingRowId(null);
  };

  const handleDeleteClick = (
    row: CarrierRestrictionRecord,
    node: IRowNode<CarrierRestrictionRecord>,
  ) => {
    setRowToDelete(row);
    if (node.id === editingRowId()) {
      handleCancelEdit();
    }
    openDialogBox(DELETE_RESTRICTION_DIALOG_ID);
  };

  const handleConfirmDelete = () => {
    const row = rowToDelete();
    if (row) {
      gridRef()?.api.applyTransaction({ remove: [row] });
    }
    setRowToDelete(null);
    closeDialogBox(DELETE_RESTRICTION_DIALOG_ID);
  };

  const columnDefs: ColDef[] = [
    {
      field: 'carrierName',
      headerName: 'Carrier',
      minWidth: 180,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: { data: CarrierRestrictionRecord }) => (
        <div class="flex flex-col">
          <span class="font-semibold">{params.data.carrierName}</span>
          <span class="text-xs text-gray-500">{`MC: ${params.data.carrierMc} | DOT: ${params.data.carrierDot}`}</span>
        </div>
      ),
    },
    {
      field: 'restrictionType',
      headerName: 'Restriction Type',
      minWidth: 150,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: {
        value: CarrierRestrictionRecord['restrictionType'];
      }) => (
        <span
          class={`px-2 py-1 rounded-full text-xs border ${
            params.value === 'Hard Block'
              ? 'text-red-600 border-red-300 bg-red-50'
              : 'text-yellow-600 border-yellow-300 bg-yellow-50'
          }`}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'reasonType',
      headerName: 'Reason Type',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'reasonDescription',
      headerName: 'Reason Description',
      minWidth: 260,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'effectiveFrom',
      headerName: 'Effective Period',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: { data: CarrierRestrictionRecord }) => {
        const from = DateTime.fromISO(params.data.effectiveFrom).toFormat(
          'MM/dd/yyyy',
        );
        const to = params.data.effectiveTo
          ? DateTime.fromISO(params.data.effectiveTo).toFormat('MM/dd/yyyy')
          : 'No End Date';
        return <span>{`${from} - ${to}`}</span>;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 110,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: {
        value: CarrierRestrictionRecord['status'];
        data: CarrierRestrictionRecord;
        node: IRowNode<CarrierRestrictionRecord>;
      }) => (
        <div class="flex items-center gap-2">
          <Switch
            checked={params.value === 'Active'}
            onChange={(checked: boolean) => {
              params.node.setData({
                ...params.data,
                status: checked ? 'Active' : 'Inactive',
              });
            }}
            dataTestId={`carrier-restriction-status-switch-${params.node.id}`}
          />
          <span
            class={
              params.value === 'Active'
                ? 'text-[#026EA1] font-medium'
                : 'text-gray-400'
            }
          >
            {params.value}
          </span>
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      autoHeight: true,
      cellStyle,
      sortable: false,
      filter: false,
      cellRenderer: (params: {
        data: CarrierRestrictionRecord;
        node: IRowNode<CarrierRestrictionRecord>;
      }) => (
        <div class="flex gap-3 items-center text-[#026EA1]">
          <PencilIcon
            style={{
              'font-size': '1.5rem',
              cursor: 'pointer',
            }}
            onClick={() => handleEditClick(params.data, params.node)}
            dataTestId={`carrier-restriction-edit-icon-${params.node.id}`}
          />
          <CloseCircleIcon
            style={{
              'font-size': '1.5rem',
              color: '#B00020',
              cursor: 'pointer',
            }}
            onClick={() => handleDeleteClick(params.data, params.node)}
            dataTestId={`carrier-restriction-delete-icon-${params.node.id}`}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <CollapsibleCard
        startTitle="Carrier Restrictions (Customer Specific)"
        class="mb-2"
      >
        <Notification
          type="info"
          style={{ 'background-color': '#E6F4F9' }}
          text={CARRIER_RESRICTION_NOTIFCATION}
        />
        <div class="add-restriction-section">
          <form ref={form}>
            <div class="text-lg font-semibold mt-2">
              {editingRowId() ? 'Update Restriction' : 'Add Restriction'}
            </div>
            <div class="grid grid-cols-12 gap-4 items-start mt-2">
              <div class="col-span-3">
                <AutoComplete<CarrierAutoCompleteModel>
                  id="carrier-list-search"
                  endpoint="carrier/autocomplete/?term"
                  label="Carrier Search"
                  required
                  placeholder="Search by Name, MC, DOT"
                  dataTestId="carrier-list-search-input"
                  responseFieldName="name"
                  value={data().carrierName || ''}
                  onItemSelect={(item) => {
                    setFields('carrierName', item.name || '');
                    setFields('carrierMc', item.mc || '');
                    setFields('carrierDot', item.dot || '');
                  }}
                  onClearValue={() => {
                    setFields('carrierName', '');
                    setFields('carrierMc', '');
                    setFields('carrierDot', '');
                  }}
                  error={errors().carrierName}
                  minCharacters={0}
                />
              </div>
              <div class="col-span-5">
                <RadioGroup
                  label="Restriction Type"
                  required
                  value={data().restrictionType}
                  options={[
                    {
                      label: (
                        <div class="flex flex-col mt-2">
                          <span class="text-md">Hard Block</span>
                          <span class="text-xs">
                            Prevents carrier from being assigned.
                          </span>
                        </div>
                      ),
                      value: 'Hard Block',
                    },
                    {
                      label: (
                        <div class="flex flex-col mt-2">
                          <span class="text-md">Warning</span>
                          <span class="text-xs">
                            Allows assignment after user acknowledgement.
                          </span>
                        </div>
                      ),
                      value: 'Warning',
                    },
                  ]}
                  onChange={(value) => setFields('restrictionType', value)}
                  labelClass="mb-[-6px] text-xs"
                />
                <div class="grid grid-cols-2 gap-2 mt-3">
                  <DatePicker
                    required
                    label="Effective From"
                    value={data().effectiveFrom}
                    handleChange={(value: string) => {
                      setFields('effectiveFrom', value);
                    }}
                    error={errors().effectiveFrom}
                    dataTestId="effectiveFrom"
                  />
                  <div>
                    <DatePicker
                      label="Effective To"
                      optional
                      value={data().effectiveTo}
                      handleChange={(value: string) => {
                        setFields('effectiveTo', value);
                      }}
                      error={errors().effectiveTo}
                      dataTestId="effectiveTo"
                    />
                    <span class="text-xs">
                      Leave blank for an ongoing restriction.
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-span-4">
                <SelectInput
                  label="Reason Type"
                  required
                  menuItems={REASON_TYPE_OPTIONS}
                  value={data().reasonType}
                  error={errors().reasonType as unknown as string}
                  onChange={(value) => setFields('reasonType', value)}
                  dataTestId="flag-popup-state-select"
                />
                <div class="mt-2">
                  <TextField
                    multiline
                    label="Description"
                    value={data().reasonDescription}
                    rows={3}
                    error={errors().reasonDescription}
                    onChange={(data) => setFields('reasonDescription', data)}
                    maxLength={5000}
                  />
                </div>
              </div>
            </div>
            <div class="flex justify-end gap-2 mt-4">
              <Show when={editingRowId()}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </Show>
              <Button type="submit">
                {editingRowId() ? 'Update Restriction' : 'Add Restriction'}
              </Button>
            </div>
          </form>
        </div>
        <div class="ag-theme-alpine mt-4">
          <AgGridSolid
            ref={setGridRef}
            columnDefs={columnDefs}
            rowData={DUMMY_CARRIER_RESTRICTIONS}
            domLayout="autoHeight"
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              filter: true,
              resizable: true,
            }}
            animateRows={true}
            getRowStyle={getRowGroupStyle}
            sideBar={{
              toolPanels: [columnsToolPanel, filtersToolPanel],
            }}
          />
        </div>
        <ConfirmationDialogBox
          isDialogOpen={checkIfDialogBoxIsOpen(DELETE_RESTRICTION_DIALOG_ID)}
          closeDialogBox={() => {
            setRowToDelete(null);
            closeDialogBox(DELETE_RESTRICTION_DIALOG_ID);
          }}
          dataTestId="carrier-restriction-delete-dialog"
          id={DELETE_RESTRICTION_DIALOG_ID}
          title="Are you sure you want to delete this carrier restriction?"
          onSubmit={handleConfirmDelete}
          onSubmitText="Delete"
        />
      </CollapsibleCard>
    </div>
  );
};

export default CarrierRestrictions;
